import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import Fuse from 'fuse.js'

export interface Composer {
  id: string
  name: string
  birth?: number
  death?: number
  searchTerms: string[]
  popularity: number
  source?: 'wikidata' | 'freetext' // Indicates if this is a freetext entry
}

interface ComposerDataset {
  version: string
  generated: string
  count: number
  composers: Composer[]
}

/**
 * Hook to load the static composer dataset
 */
export const useComposerDataset = () => {
  return useQuery<ComposerDataset>({
    queryKey: ['composers-dataset'],
    queryFn: async () => {
      const response = await fetch('/data/composers.json')
      if (!response.ok) {
        throw new Error(`Failed to load composer dataset: ${response.statusText}`)
      }
      return response.json()
    },
    staleTime: Infinity, // Never refetch - static dataset
    gcTime: Infinity, // Keep in cache forever (formerly cacheTime)
    retry: 3, // Retry 3 times if loading fails
  })
}

/**
 * Hook for client-side composer autocomplete with fuzzy search
 *
 * @param searchTerm - The search term to match against composer names
 * @param limit - Maximum number of results to return (default: 10)
 * @returns Array of matching composers, sorted by relevance and popularity
 */
export const useComposerAutocomplete = (searchTerm: string, limit: number = 10): Composer[] => {
  const { data: dataset } = useComposerDataset()

  const results = useMemo(() => {
    // Return empty array if no dataset or search term is too short
    if (!dataset?.composers || searchTerm.length < 2) {
      return []
    }

    // Normalize function to remove accents and lowercase
    const normalize = (str: string) =>
      str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

    // Normalize search term and split into tokens
    const searchTokens = normalize(searchTerm)
      .split(/\s+/)
      .filter((token) => token.length > 0)

    // First pass: Filter composers where ALL search tokens are present as substrings
    // This allows "dvor" to match "Dvořák" and "bach" to match "Johann Sebastian Bach"
    const tokenMatches = dataset.composers.filter((composer) => {
      const normalizedName = normalize(composer.name)
      const normalizedSearchTerms = composer.searchTerms.map(normalize).join(' ')
      const combinedText = `${normalizedName} ${normalizedSearchTerms}`

      // Check if all search tokens are present in the composer
      return searchTokens.every((token) => combinedText.includes(token))
    })

    // If we have token matches, return them sorted by popularity
    if (tokenMatches.length > 0) {
      return tokenMatches.sort((a, b) => b.popularity - a.popularity).slice(0, limit)
    }

    // Fallback to Fuse.js fuzzy search if no token matches
    // This handles typos and more complex fuzzy matching
    const fuse = new Fuse(dataset.composers, {
      keys: [
        { name: 'name', weight: 2 },
        { name: 'searchTerms', weight: 1 },
      ],
      threshold: 0.4,
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 2,
      isCaseSensitive: false,
      getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path)
        if (typeof value === 'string') {
          return normalize(value)
        }
        return value
      },
    })

    const fuseResults = fuse.search(normalize(searchTerm), { limit })
    return fuseResults.map((result) => result.item)
  }, [dataset, searchTerm, limit])

  return results
}

/**
 * Utility function to format composer display name with years
 *
 * @param composer - The composer object
 * @returns Formatted string like "Ludwig van Beethoven (1770-1827)"
 */
export const formatComposerName = (composer: Composer): string => {
  const { name, birth, death } = composer

  if (birth && death) {
    return `${name} (${birth}–${death})`
  } else if (birth) {
    return `${name} (b. ${birth})`
  } else {
    return name
  }
}
